import type { GlobalState } from "../../types/stateTypes";
import { buildResultsPayload } from "./buildResultsPayload";
import type { OpenlifterBundleFile } from "./types";
import { defaultWrpfLiveState } from "../live/platformLiveProfile";
import { wrapsOnlyAffectSquatEnabled } from "../../logic/equipmentForEvent";

export function sanitizeStateForOpenlifterFile(state: GlobalState): GlobalState {
  return {
    ...state,
    meet: {
      ...state.meet,
      wrapsOnlyAffectSquat: wrapsOnlyAffectSquatEnabled(state.meet),
    },
    wrpfSession: {
      ...state.wrpfSession,
      callbackToken: null,
    },
    wrpfLive: defaultWrpfLiveState(),
  };
}

/** Adds `_wrpf.precomputedResults` for Mode B1 bundle upload without porting coefficients to WRPF. */
export function buildExportBundle(state: GlobalState): OpenlifterBundleFile & GlobalState {
  const exportState = sanitizeStateForOpenlifterFile(state);
  const tournamentId = state.wrpfSession.tournamentId;
  if (!tournamentId) {
    return { ...exportState } as OpenlifterBundleFile & GlobalState;
  }

  const precomputedResults = buildResultsPayload(state, "final");
  const wrpfMeta = {
    schemaVersion: 1 as const,
    tournamentId,
    tournamentName: state.meet.name,
    tournamentScope: state.wrpfRecords.tournamentScope ?? undefined,
    recordCategories: [...state.wrpfRecords.recordCategories],
    federation: state.meet.federation,
    callback: state.wrpfSession.callbackUrl
      ? {
          resultsUrl: state.wrpfSession.callbackUrl,
          heartbeatUrl: "",
          token: state.wrpfSession.callbackToken ?? "",
          expiresAt: state.wrpfSession.tokenExpiresAt ?? "",
        }
      : null,
    entryAnchors: [...state.wrpfSession.entryAnchors],
    currentRecords: [...state.wrpfRecords.currentRecords],
    sponsors: [...(state.wrpfSession.sponsors ?? [])],
    precomputedResults,
  };

  return {
    ...exportState,
    _wrpf: wrpfMeta,
  } as OpenlifterBundleFile & GlobalState;
}

/**
 * Lo que se sube a la web ("Respaldar en la web" y "Enviar resultados a la web"): el export sin los récords
 * vigentes. La web no los usa, y son ~19 mil récords y estándares (~6 MB) que el export lleva dos veces: el
 * pedido pasaba el límite de 6 MB de Netlify (413). El archivo .openlifter los sigue llevando.
 */
export function buildWebUploadBundle(state: GlobalState): OpenlifterBundleFile & GlobalState {
  const bundle = buildExportBundle(state);
  return {
    ...bundle,
    wrpfRecords: { ...bundle.wrpfRecords, currentRecords: [] },
    ...(bundle._wrpf ? { _wrpf: { ...bundle._wrpf, currentRecords: [] } } : {}),
  };
}

export function bundleHasMeet(state: GlobalState): boolean {
  return state.meet.name !== "";
}
